import { useState } from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { fetchPostOffersSelect } from '@iwoca/lapi-client/edge';

import OfferCard from './OfferCard';
import { getDurationInYears } from '../../helpers/product.helpers';
import { useContextStateKey } from '../../hooks/useContextStateKey';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { TGetOffersResponse, TOfferPill } from '../../types';
import { getAmplitudeDefaultEventProperties } from '../../utils/amplitudeEvents';
import logger from '../../utils/logger';
import { Button } from '../Button/Button';
import IconWithText from '../IconWithText/IconWithText';

const IndicativeOfferCard = ({
  max_amount,
  probability_of_offer,
  offer_id,
  product_type,
  data_requirements,
  duration,
}: Pick<
  TGetOffersResponse['data']['offers'][number],
  | 'max_amount'
  | 'probability_of_offer'
  | 'offer_id'
  | 'product_type'
  | 'data_requirements'
  | 'duration'
>) => {
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const stateKey = useContextStateKey();
  const { navigateSignup } = useSignupRouterUK();

  const handleClick = async () => {
    setIsLoading(true);

    if (!stateKey) {
      return null;
    }

    tracking.ampli.offerSelected({
      selectedOfferProduct: product_type,
      ...getAmplitudeDefaultEventProperties(),
    });

    try {
      await fetchPostOffersSelect({ offerId: offer_id });
      const documentRequirements = data_requirements.find(
        (requirement) => requirement.category === 'documents',
      );

      const hasDocumentsRequired = documentRequirements?.actions.find(
        (action) =>
          action.status === 'required' && action.is_optional === false,
      );

      const nextPage = hasDocumentsRequired ? 'documents' : 'decision';

      return navigateSignup({ route: nextPage });
    } catch {
      setError('Something went wrong, please try again.');
    }

    setIsLoading(false);
  };

  const pills: TOfferPill[] = [
    { key: 'eligibility', variant: 'london', value: 'Eligible' },
  ];

  if (probability_of_offer === 'high_probability_of_offer') {
    pills.push({
      key: 'probability',
      variant: 'green',
      value: ' High chance of approval',
    });
  }

  if (product_type !== 'credit_facility' && product_type !== 'flexi24') {
    logger.error(
      `Unknown product type for indicative or sample offer card. Product type of ${product_type}.`,
    );
    return null;
  }

  const durationLength =
    getDurationInYears(duration) > 1
      ? `${getDurationInYears(duration)} years`
      : `${getDurationInYears(duration)} year`;

  return (
    <OfferCard
      pills={pills}
      amount={max_amount}
      productDetails={
        <ul>
          <li>
            <IconWithText icon="calendarOutline">
              Up to {durationLength}
            </IconWithText>
          </li>
        </ul>
      }
      button={
        <Button
          loading={isLoading}
          onClick={handleClick}
          data-testid={`upload-documents-${product_type}`}
          size="small"
        >
          Continue to upload docs
        </Button>
      }
      error={error}
    />
  );
};

export default IndicativeOfferCard;
